import '../Growl.css';
import { isDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import type { ReactElement } from 'react';
import { Toast } from 'react-bootstrap';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { useCurrentUserQuery } from '@/api/user';
import { GrowlError } from '@/App/Growl/shared/GrowlError';
import { GrowlItem } from '@/App/Growl/shared/GrowlItem';
import { getExecutionColorAndIcon } from '@/App/Growl/utilities';
import { Panel } from '@/components/ui/Panel';
import type { GrowlOption } from '@/store/growl/state';
import type { ForwardAccumulatorDealExecution } from '@/store/growl/state/forwardAccumulator.ts';
import { formatUserInputtedDate, toUtc } from '@/utils/date.ts';

interface GrowlForwardAccumulatorProps {
  growlItem: ForwardAccumulatorDealExecution;
  options?: GrowlOption;
}

export function GrowlForwardAccumulator({ growlItem, options }: GrowlForwardAccumulatorProps): ReactElement {
  const { data: currentUser } = useCurrentUserQuery();
  const { locale } = useIntl();
  const { status: executionStatus, data } = growlItem;

  const accountName = isDefined(currentUser)
    ? currentUser.clients.find((client) => client.companyId === data.accountId)?.companyName
    : undefined;

  const isExternalUser = isDefined(currentUser) && currentUser.userType === 'Trader';
  const isInternalSales = isDefined(currentUser) && currentUser.userType === 'Internal Sales';

  const [color, icon] = getExecutionColorAndIcon(executionStatus);

  const premium = Number.parseFloat(data.premium);
  const premiumValue = Math.abs(premium);
  const premiumWay = premium >= 0 ? 'pay' : 'receive';
  const premiumCurrency = data.premiumCurrencyIndex === 1 ? data.currency1 : data.currency2;

  const { closeable = true } = options ?? {};

  return (
    <>
      <Toast.Header
        className={`bg-${color} text-${color} bg-opacity-10`}
        closeButton={closeable}
        data-e2e="growl-header"
      >
        <i className="icon icon-sm me-2">{icon}</i>
        <strong className="me-auto">
          <FormattedMessage id={`growl.status.${executionStatus}`} defaultMessage={executionStatus || 'Unknown'} />
        </strong>
      </Toast.Header>
      <Toast.Body>
        <div data-e2e="growl-message" className="fw-medium pt-1 text-truncate text-nowrap">
          <FormattedMessage
            id={`growl.FxForwardAccumulator.${data.way}.${isExternalUser ? 'externalUser' : 'internalUser'}.message`}
            values={{ currencyPair: `${data.currency1}/${data.currency2}`, ccy1: data.currency1, strike: data.strike }}
          />
        </div>

        {growlItem.status === 'rejected' && (
          <GrowlError
            errorCode={growlItem.errorCode}
            multipassErrorCode={growlItem.multipassErrorCode}
            reason={growlItem.reason}
            sessionId={growlItem.sessionId}
          />
        )}

        <Panel labelId={'growl.FxForwardAccumulator.dealDetails'} data-e2e="growl-details-toggle-button">
          <HStack horizontalAlignment="full" className="border-bottom py-8px fs-12">
            <span className="text-secondary" data-e2e="growl-client-way">
              <FormattedMessage id={`growl.FxForwardAccumulator.premium.${premiumWay}`} values={{ isInternalSales }} />
            </span>

            <span className="text-truncate text-end" data-e2e="premium-message">
              <FormattedNumber value={premiumValue} /> {premiumCurrency}
            </span>
          </HStack>
          <GrowlItem
            labelId={`growl.FxForwardAccumulator.action.${data.way}`}
            labelValues={{ isInternalSales }}
            data-e2e-label="way-message"
            data-e2e="notional"
          >
            <FormattedNumber value={data.notional} />
            &nbsp;{data.notionalCurrencyIndex === 1 ? data.currency1 : data.currency2}&nbsp;/&nbsp;
            {data.amountMode}
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.accuType" data-e2e="accuType">
            {data.accuType}
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.strike" data-e2e="strike">
            {data.strike}
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.akoTrigger" data-e2e="akoTrigger">
            {data.akoTrigger}
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.ekiTrigger" data-e2e="ekiTrigger">
            {data.ekiTrigger}
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.step" data-e2e="step">
            {data.step}
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.firstFixing" data-e2e="firstFixingDate">
            {formatUserInputtedDate(toUtc(new Date(data.firstFixing.date)), locale)} ({data.firstFixing.tenor})
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.expiry" data-e2e="expiryDate">
            {formatUserInputtedDate(toUtc(new Date(data.expiry.date)), locale)} ({data.expiry.tenor})
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.fixingFrequency" data-e2e="fixingFrequency-message">
            {data.fixingFrequency} ({data.numberOfFixings})
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.account" data-e2e="growl-account">
            {accountName}
          </GrowlItem>
          <GrowlItem labelId="growl.FxForwardAccumulator.tradeDate" data-e2e="tradeDate">
            <FormattedDate
              value={data.tradeDate}
              day="2-digit"
              month="short"
              year="numeric"
              hour="2-digit"
              minute="numeric"
              hour12
            />
          </GrowlItem>
          {growlItem.status !== 'rejected' && (
            <GrowlItem labelId="growl.FxForwardAccumulator.tradeId" data-e2e="tradeId-message">
              {growlItem.status === 'confirmed' ? (
                growlItem.tradeIds[0]
              ) : (
                <FormattedMessage id="growl.Cash.tradeId.awaitingConfirmation" />
              )}
            </GrowlItem>
          )}
        </Panel>
      </Toast.Body>
    </>
  );
}
